import { z } from 'zod';
import { isoDateSchema, montantSchema, nullableText, paginationSchema, round2 } from './common.ts';

/** Taux de TVA par défaut — la constante unique de la règle 4. */
export const TAUX_TVA_DEFAUT = 0.2;
export const VILLE_DEFAUT = 'Salé';
export const PREFIXE_REFERENCE = 'FAC';
export const LONGUEUR_NUMERO = 4;

export const FACTURE_STATUTS = ['BROUILLON', 'EMISE', 'ANNULEE'] as const;
export const factureStatutSchema = z.enum(FACTURE_STATUTS);
export type FactureStatut = z.infer<typeof factureStatutSchema>;

export const FACTURE_STATUT_LABELS: Record<FactureStatut, string> = {
  BROUILLON: 'Brouillon',
  EMISE: 'Émise',
  ANNULEE: 'Annulée',
};

/* ------------------------------------------------------------------ */
/* Règle 1 — numérotation                                              */
/* ------------------------------------------------------------------ */

/** `FAC-2026-0007`. Le numéro d'ordre pilote la référence par défaut. */
export function formatReference(annee: number, numeroOrdre: number): string {
  return `${PREFIXE_REFERENCE}-${annee}-${String(numeroOrdre).padStart(LONGUEUR_NUMERO, '0')}`;
}

/** Séquence sans trou : le suivant est toujours max + 1. */
export function prochainNumeroOrdre(maxActuel: number | null | undefined): number {
  return (maxActuel ?? 0) + 1;
}

export const referenceSchema = z
  .string()
  .trim()
  .min(1, 'La référence est obligatoire')
  .max(50, 'La référence est trop longue');

/* ------------------------------------------------------------------ */
/* Règle 2 — immuabilité                                               */
/* ------------------------------------------------------------------ */

/** Une facture n'est modifiable qu'en brouillon. */
export function factureEstModifiable(statut: FactureStatut): boolean {
  return statut === 'BROUILLON';
}

/** On n'émet que depuis un brouillon. */
export function factureEstEmettable(statut: FactureStatut): boolean {
  return statut === 'BROUILLON';
}

/** On n'annule qu'une facture émise (un brouillon se supprime). */
export function factureEstAnnulable(statut: FactureStatut): boolean {
  return statut === 'EMISE';
}

/* ------------------------------------------------------------------ */
/* Règle 4 — totaux                                                    */
/* ------------------------------------------------------------------ */

export type LigneCalcul = {
  quantite: number;
  prix_unitaire_ht: number;
  taux_tva: number;
};

export type LigneCalculee = LigneCalcul & {
  total_ligne_ht: number;
  total_ligne_tva: number;
  total_ligne_ttc: number;
};

export type TotauxFacture = {
  lignes: LigneCalculee[];
  total_ht: number;
  total_tva: number;
  total_ttc: number;
};

/**
 * HT ligne = quantité × PU HT ; la TVA est calculée **par ligne** au taux de la
 * ligne, puis les totaux sont les sommes. Tout est arrondi à 2 décimales.
 */
export function calculerTotauxFacture(lignes: readonly LigneCalcul[]): TotauxFacture {
  const calculees = lignes.map<LigneCalculee>((l) => {
    const total_ligne_ht = round2(l.quantite * l.prix_unitaire_ht);
    const total_ligne_tva = round2(total_ligne_ht * l.taux_tva);
    return {
      ...l,
      total_ligne_ht,
      total_ligne_tva,
      total_ligne_ttc: round2(total_ligne_ht + total_ligne_tva),
    };
  });

  const total_ht = round2(calculees.reduce((acc, l) => acc + l.total_ligne_ht, 0));
  const total_tva = round2(calculees.reduce((acc, l) => acc + l.total_ligne_tva, 0));

  return { lignes: calculees, total_ht, total_tva, total_ttc: round2(total_ht + total_tva) };
}

/* ------------------------------------------------------------------ */
/* Schémas                                                             */
/* ------------------------------------------------------------------ */

export const ligneFactureInputSchema = z.object({
  product_id: nullableText,
  designation: z.string().trim().min(1, 'La désignation est obligatoire').max(300),
  quantite: z.coerce.number().int().min(1, 'La quantité doit être au moins 1'),
  prix_unitaire_ht: montantSchema.pipe(z.number().min(0, 'Le prix ne peut pas être négatif')),
  taux_tva: z.coerce.number().min(0).max(1).default(TAUX_TVA_DEFAUT),
});
export type LigneFactureInput = z.infer<typeof ligneFactureInputSchema>;

const ORDONNANCE_CHAMPS = [
  'od_loin_sph',
  'od_loin_cyl',
  'og_loin_sph',
  'og_loin_cyl',
  'od_pres_sph',
  'od_pres_cyl',
  'og_pres_sph',
  'og_pres_cyl',
] as const;
export type OrdonnanceChamp = (typeof ORDONNANCE_CHAMPS)[number];

export const ordonnanceFactureSchema = z.object(
  Object.fromEntries(ORDONNANCE_CHAMPS.map((c) => [c, nullableText])) as Record<
    OrdonnanceChamp,
    typeof nullableText
  >,
);
export type OrdonnanceFacture = z.infer<typeof ordonnanceFactureSchema>;

export const factureInputSchema = z
  .object({
    /** Modifiable uniquement tant que la facture est en brouillon (règle 1). */
    reference: referenceSchema.optional(),
    date_facture: isoDateSchema,
    ville: z.string().trim().default(VILLE_DEFAUT),
    client_id: nullableText,
    client_nom: z.string().trim().min(1, 'Le nom du client est obligatoire').max(200),
    client_ice: nullableText,
    lignes: z.array(ligneFactureInputSchema).default([]),
    /** Commande d'origine quand la facture est générée depuis /commandes. */
    commande_id: nullableText,
  })
  .merge(ordonnanceFactureSchema.partial());
export type FactureInput = z.infer<typeof factureInputSchema>;

export const factureUpdateSchema = factureInputSchema.partial();
export type FactureUpdate = z.infer<typeof factureUpdateSchema>;

export const annulerFactureSchema = z.object({
  motif: z.string().trim().min(1, "Le motif d'annulation est obligatoire").max(500),
});
export type AnnulerFactureInput = z.infer<typeof annulerFactureSchema>;

export const factureListQuerySchema = paginationSchema.extend({
  q: z.string().trim().optional(),
  statut: factureStatutSchema.optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  client_id: z.string().trim().optional(),
});
export type FactureListQuery = z.infer<typeof factureListQuerySchema>;

export type LigneFacture = {
  id: string;
  facture_id: string;
  product_id: string | null;
  designation: string;
  quantite: number;
  prix_unitaire_ht: number;
  taux_tva: number;
  total_ligne_ht: number;
};

export type Facture = {
  id: string;
  numero_ordre: number;
  reference: string;
  date_facture: string;
  ville: string;
  client_id: string | null;
  client_nom: string;
  client_ice: string | null;
  od_loin_sph: string | null;
  od_loin_cyl: string | null;
  og_loin_sph: string | null;
  og_loin_cyl: string | null;
  od_pres_sph: string | null;
  od_pres_cyl: string | null;
  og_pres_sph: string | null;
  og_pres_cyl: string | null;
  total_ht: number | null;
  total_tva: number | null;
  total_ttc: number | null;
  montant_lettres: string | null;
  statut: FactureStatut;
  motif_annulation: string | null;
  created_at: string;
  emise_at: string | null;
  lignes: LigneFacture[];
};

/** Coordonnées légales imprimées en pied de facture. */
export const SOCIETE = {
  nom: 'SIRAJOPTIC',
  adresse: '630, Rue Sidi Abdellah Hay Inbiaat - Salé - sirajoptic@gmail.com - 06.61.49.41.31',
  legal:
    'R.C: 25239 - IF: 18793369 - TP: 29198665 - CNSS: 4884070 - ICE: 001637844000027 - INPE: 105008056',
  couleur: '#2E9E4F',
} as const;
